import {parseDate} from 'common/date';
import Money from 'common/Money';
import {Check, CircleAlert, CircleDashed, CircleDot, Copy} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {AccountInlineDisplay} from 'src/AccountInlineDisplay';
import {AmountDisplay} from 'src/AmountDisplay';
import {useLongPress} from 'src/hooks/useLongPress';
import {formatListDate} from 'src/lib/displayDate';
import {cn} from 'src/lib/utils';
import type {BasicRawTransactionFragment} from '@transactions/graphql';
import type {LucideIcon} from 'lucide-react';

type Props = {
  rawTransaction: BasicRawTransactionFragment;
  selected?: boolean;
  onClick?: () => void;
  onLongPress?: () => void;
  href?: string;
  showProcessedStatus?: boolean;
  // Render a leading checklist column: a green check for reconciled rows
  // (which are also dimmed), an empty slot for unreconciled ones. Keeping the
  // marks in one column makes gaps jump out when scanning down the timeline.
  reconciliationChecklist?: boolean;
  // Checklist-only: this unreconciled row is dated before the latest
  // reconciled anchor, i.e. a reconciliation passed over it without claiming
  // it — a discrepancy, unlike rows simply not yet reached by reconciliation.
  missedReconciliation?: boolean;
  // During an active reconcile session: this row is dated outside the period
  // being reconciled. Still selectable (late-posting rows can belong to the
  // statement — the server only checks the sum) but marked so boundary rows
  // are distinguishable, mirroring the reconcile modal.
  outsidePeriod?: boolean;
  // Single-line layout without the account chip, sized to sit comfortably
  // between the reconcile timeline's anchor rows. The account is implied
  // there (the timeline is always scoped to one account).
  compact?: boolean;
  // When provided, renders in place of the default amount display — used to
  // show an editable allocation control per row.
  renderAmount?: (rawTransaction: BasicRawTransactionFragment) => React.ReactNode;
  // Render a copy-to-clipboard affordance beside the description. Used where
  // the row is source material for a form (e.g. the create-transaction
  // modal, where the payee no longer prefills from the descriptor).
  copyDescription?: boolean;
  // Compact layout only: an action rendered just before the date. The row
  // becomes a `group` so the action can reveal itself on hover. Used by the
  // reconcile timeline for "record a balance on this row's date".
  rowAction?: React.ReactNode;
  // Opts the row into the exit animation: false holds it open, true collapses
  // and fades it upward. Reconciling claims rows in batches, and dropping a
  // statement's worth of them between two frames reads as a glitch rather than
  // as the result of the action. Leave undefined in lists that remove rows
  // instantly — no transition machinery is attached then.
  leaving?: boolean;
  // Opts the row into select mode's leading check. Undefined: no check column
  // at all. Defined, the column takes no space while false and grows in (the
  // row's content sliding over) when true. While false, a tap on the row's
  // leading edge (LEADING_EDGE) stands in for a long press.
  selectMode?: boolean;
};

// Compact status indicator: an icon with a hover tooltip instead of a labeled
// pill. Statuses are exception-only — "done" states (fully allocated,
// reconciled) render nothing, so a caught-up row stays completely clean and
// any icon at all means there is work to do.
const StatusIcon = ({
  icon: Icon,
  label,
  className,
}: {
  icon: LucideIcon;
  label: string;
  className: string;
}) => (
  <span title={label} aria-label={label} className="inline-flex items-center">
    <Icon aria-hidden className={cn('size-4', className)} />
  </span>
);

// Fixed-size (size-5 sits inside the description's line box) so the copied
// feedback is an icon swap, never a row-height change.
// Selecting takes a hold, so the row answers the hold itself: its background
// fills toward the selection tint over the long-press duration.
const HOLD_FILL =
  'active:bg-blue-600/10 active:duration-[400ms] dark:active:bg-blue-400/15';

// A tap this close to the row's left edge (its padding, and the first letters
// of the description) starts selecting, as a long press does: an unmarked
// shortcut to the check column that is not there yet.
const LEADING_EDGE = 44;

const SelectCheck = ({open, selected}: {open: boolean; selected: boolean}) => (
  <span
    aria-hidden
    className={cn(
      'flex shrink-0 items-center overflow-hidden transition-[width,opacity] duration-200 ease-out motion-reduce:transition-none',
      open ? 'w-8 opacity-100' : 'w-0 opacity-0',
    )}
  >
    <span
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full border-[1.5px] transition-[background-color,border-color] duration-150 ease-out motion-reduce:transition-none',
        selected
          ? 'border-blue-600 bg-blue-600 text-white dark:border-blue-500 dark:bg-blue-500'
          : 'border-muted-foreground/60',
      )}
    >
      {selected && <Check className="size-3.5" strokeWidth={3} />}
    </span>
  </span>
);

export const CopyDescriptionButton = ({text}: {text: string}) => {
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timeout);
  }, [copied]);

  return (
    <button
      type="button"
      title={copied ? 'Copied' : 'Copy description'}
      aria-label="Copy description"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        // Only report success once the write lands — a denied or
        // unavailable clipboard must not flash "Copied" over nothing.
        void navigator.clipboard?.writeText(text).then(
          () => setCopied(true),
          () => {},
        );
      }}
      className="text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 flex size-5 shrink-0 cursor-pointer items-center justify-center rounded"
    >
      {copied ? (
        <Check className="size-3.5 text-positive" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </button>
  );
};

type AllocationStatus = 'allocated' | 'partial' | 'unallocated';

const allocationStatus = (
  rawTransaction: BasicRawTransactionFragment,
): AllocationStatus => {
  const {amount, remainingAmount} = rawTransaction;
  if (remainingAmount === 0) return 'allocated';
  // Legacy rows (null remainingAmount) are treated as unallocated, matching
  // the previous "Unprocessed" pill behavior.
  if (remainingAmount === null || remainingAmount === amount) {
    return 'unallocated';
  }
  return 'partial';
};

export const RawTransactionRow = (props: Props) => {
  const {
    rawTransaction,
    selected,
    onClick,
    onLongPress,
    href,
    showProcessedStatus = false,
    reconciliationChecklist = false,
    missedReconciliation = false,
    outsidePeriod = false,
    compact = false,
    renderAmount,
    copyDescription = false,
    rowAction,
    leaving,
    selectMode,
  } = props;
  const isFuture = parseDate(rawTransaction.date) > DateTime.now();
  const isReconciled = Boolean(rawTransaction.reconciliation);

  // USD second line for foreign rows, from the server's draw preview (what
  // allocating this row now would cost).
  const approxHome =
    rawTransaction.currency !== 'USD' &&
    rawTransaction.homeAmountPreview != null
      ? Money.fromInt(rawTransaction.homeAmountPreview, 'USD')
      : null;

  const {longPressProps, consumeLongPress} = useLongPress(onLongPress);

  const handleClick = React.useCallback(
    (event: React.MouseEvent) => {
      if (consumeLongPress()) {
        event.preventDefault();
        return;
      }
      if (
        selectMode === false &&
        onLongPress != null &&
        event.clientX - event.currentTarget.getBoundingClientRect().left <
          LEADING_EDGE
      ) {
        event.preventDefault();
        onLongPress();
        return;
      }
      onClick?.();
    },
    [consumeLongPress, onClick, onLongPress, selectMode],
  );

  const className = cn(
    'box-border w-full select-none',
    // Only when there is something to reveal — `group` is inherited by every
    // descendant's group-* utilities, so it isn't free to apply everywhere.
    rowAction && 'group',
    compact
      ? 'flex items-center gap-x-4 px-4 py-2'
      : cn(
          'grid items-center gap-x-5 gap-y-1.5',
          reconciliationChecklist
            ? '[grid-template-columns:auto_1fr_auto] [grid-template-areas:"check_left_right"]'
            : '[grid-template-columns:1fr_auto] [grid-template-areas:"left_right""left_right"]',
          'px-4 py-3',
        ),
    'transition-colors',
    'not-last:border-border not-last:border-b',
    // Blue tint rather than a neutral: rows render both on the white page
    // background and on bg-muted inbox cards, where gray-on-gray selection
    // is invisible.
    selected
      ? 'bg-blue-600/10 dark:bg-blue-400/15'
      : cn('hover:bg-muted-foreground/10', selectMode !== undefined && HOLD_FILL),
    isFuture && 'italic opacity-60',
    reconciliationChecklist && isReconciled && 'opacity-45',
    // max-h-0 alone does NOT reach zero. box-border means max-height bounds the
    // border box, but the content box floors at zero, so the used height
    // bottoms out at the padding plus border — ~17px per row, which for a
    // claimed batch is a visible gap that then snaps shut at unmount. The
    // padding and border have to animate to zero too. (not-last: on the border
    // so it beats the not-last:border-b above at equal specificity.)
    //
    // max-h-12 is the open value: comfortably above a compact row's ~37px
    // without so much slack that the collapse looks like it starts late. All of
    // it is attached only when the caller opts in, so no other list picks up a
    // transition on every property.
    leaving !== undefined && 'overflow-hidden transition-all duration-200 ease-out',
    leaving === false && 'max-h-12',
    leaving === true &&
      'pointer-events-none max-h-0 -translate-y-1 py-0 opacity-0 not-last:border-b-0',
  );

  const checklistIcon = reconciliationChecklist && (
    <div
      className={cn(
        'flex w-4 shrink-0 items-center',
        !compact && '[grid-area:check]',
      )}
    >
      {isReconciled ? (
        <StatusIcon
          icon={Check}
          label="Reconciled"
          className="text-positive"
        />
      ) : missedReconciliation ? (
        <StatusIcon
          icon={CircleAlert}
          label="Missed by reconciliation"
          className="text-destructive"
        />
      ) : (
        <span
          className="size-4"
          title="Not yet reconciled"
          aria-label="Not yet reconciled"
        />
      )}
    </div>
  );

  const dateClassName = cn(
    'text-muted-foreground tabular-nums',
    outsidePeriod && 'italic underline decoration-dotted',
  );
  const dateTitle = outsidePeriod ? 'Outside this period' : undefined;

  const compactContent = (
    <>
      {checklistIcon}
      <div
        className="text-foreground min-w-0 flex-1 truncate text-sm"
        title={rawTransaction.description}
      >
        {rawTransaction.description}
      </div>
      {rawTransaction.benefit && (
        <span className="shrink-0 rounded-full bg-blue-800 px-2 py-0.5 text-[0.7rem] font-semibold text-white dark:bg-blue-600">
          {rawTransaction.benefit.name}
        </span>
      )}
      {rawTransaction.adjustmentForAnchorId && (
        <span
          className="shrink-0 rounded-full bg-amber-700 px-2 py-0.5 text-[0.7rem] font-semibold text-white dark:bg-amber-600"
          title="Reconciliation adjustment"
        >
          Adjustment
        </span>
      )}
      <div className="shrink-0 text-sm">
        {renderAmount ? (
          renderAmount(rawTransaction)
        ) : (
          <AmountDisplay
            amount={Money.fromInt(
              rawTransaction.amount,
              rawTransaction.currency,
            )}
            approx={approxHome}
          />
        )}
      </div>
      {rowAction}
      <div
        className={cn(dateClassName, 'w-20 shrink-0 text-right text-xs')}
        title={dateTitle}
      >
        {formatListDate(rawTransaction.date)}
      </div>
    </>
  );

  const content = compact ? (
    compactContent
  ) : (
    <>
      {checklistIcon}
      <div className="flex min-w-0 items-center [grid-area:left]">
      {selectMode !== undefined && (
        <SelectCheck open={selectMode} selected={selected ?? false} />
      )}
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <div
            className="text-foreground min-w-0 truncate"
            title={rawTransaction.description}
          >
            {rawTransaction.description}
          </div>
          {copyDescription && (
            <CopyDescriptionButton text={rawTransaction.description} />
          )}
        </div>
        <div className="text-muted-foreground flex flex-wrap items-center gap-1.5">
          <AccountInlineDisplay account={rawTransaction.account} />
          {rawTransaction.benefit && (
            <span className="rounded-full bg-blue-800 px-2 py-0.5 text-[0.7rem] font-semibold text-white dark:bg-blue-600">
              {rawTransaction.benefit.name}
            </span>
          )}
          {rawTransaction.adjustmentForAnchorId && (
            <span
              className="rounded-full bg-amber-700 px-2 py-0.5 text-[0.7rem] font-semibold text-white dark:bg-amber-600"
              title="Reconciliation adjustment"
            >
              Adjustment
            </span>
          )}
          {showProcessedStatus &&
            (() => {
              switch (allocationStatus(rawTransaction)) {
                case 'allocated':
                  return null;
                case 'partial':
                  return (
                    <StatusIcon
                      icon={CircleDot}
                      label="Partially allocated"
                      className="text-amber-600 dark:text-amber-500"
                    />
                  );
                case 'unallocated':
                  return (
                    <StatusIcon
                      icon={CircleDashed}
                      label="Unallocated"
                      className="text-destructive"
                    />
                  );
              }
            })()}
        </div>
      </div>
      </div>
      <div className="flex min-w-0 flex-col items-end justify-center gap-1 pl-3 text-right [grid-area:right]">
        {renderAmount ? (
          renderAmount(rawTransaction)
        ) : (
          <AmountDisplay
            amount={Money.fromInt(
              rawTransaction.amount,
              rawTransaction.currency,
            )}
            approx={approxHome}
          />
        )}
        <div className={dateClassName} title={dateTitle}>
          {formatListDate(rawTransaction.date)}
        </div>
      </div>
    </>
  );

  const commonProps = {
    ...longPressProps,
    onClick: handleClick,
  };

  if (href) {
    return (
      <Link to={href} className={className} {...commonProps}>
        {content}
      </Link>
    );
  }

  // A row that acts on tap without being a link still takes the keyboard:
  // focusable, and Enter or Space does what a tap does.
  const keyboardProps = onClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.target !== event.currentTarget) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          onClick();
        },
      }
    : {};

  return (
    <div
      className={cn(
        className,
        onClick && 'focus-visible:outline-ring focus-visible:outline-2 focus-visible:-outline-offset-2',
      )}
      {...commonProps}
      {...keyboardProps}
    >
      {content}
    </div>
  );
};
